import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Send, Bot, User, Loader2, History, ChevronDown, ChevronUp, ExternalLink, Copy, Check, FileDown, FileSpreadsheet, Download, Mic } from 'lucide-react'
import { clsx } from 'clsx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { accountPromptsApi, type AccountPrompt } from '@api/client'
import { useAuthStore } from '@store/authStore'
import { exportResponseToPDF, exportResponseToCSV, exportHistoryToPDF, exportHistoryToCSV, detectExportIntent, type ExportFormat } from '../utils/exportUtils'
import { useVoiceInput } from '../hooks/useVoiceInput'

interface AIAssistantProps {
  accountId: string
  accountName: string
  domain: string | null
  onClose: () => void
}

export function AIAssistant({ accountId, accountName, domain: _domain, onClose }: AIAssistantProps) {
  const { user } = useAuthStore()
  const [prompt, setPrompt] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentResponse, setCurrentResponse] = useState<AccountPrompt | null>(null)
  const [history, setHistory] = useState<AccountPrompt[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [logToHubspot, setLogToHubspot] = useState(true)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const responseRef = useRef<HTMLDivElement>(null)

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Load history when toggled on
  useEffect(() => {
    if (showHistory && history.length === 0) {
      loadHistory()
    }
  }, [showHistory])

  const loadHistory = async () => {
    setIsLoadingHistory(true)
    try {
      const response = await accountPromptsApi.getHistory(accountId, { limit: 20 })
      if (response.success && response.data) {
        setHistory(response.data.prompts)
      }
    } catch {
      console.error('Failed to load history')
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!prompt.trim() || isLoading || !user) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await accountPromptsApi.ask(accountId, {
        prompt: prompt.trim(),
        user_email: user.email,
        user_name: user.name,
        account_name: accountName,
        log_to_hubspot: logToHubspot,
      })

      if (response.success && response.data) {
        setCurrentResponse(response.data)
        // Detect export intent from the original prompt before clearing
        const format = detectExportIntent(prompt.trim())
        setDetectedFormat(format)
        setShowExportBanner(!!format)
        setPrompt('')
        // Stop voice if still listening
        if (isListening) stopListening()
        // Add to history
        setHistory(prev => [response.data!, ...prev])
        // Scroll to response
        setTimeout(() => {
          responseRef.current?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } else {
        setError(response.error || 'Failed to get AI response')
      }
    } catch {
      setError('Failed to communicate with AI assistant')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [showHistoryExportMenu, setShowHistoryExportMenu] = useState(false)
  const [detectedFormat, setDetectedFormat] = useState<ExportFormat | null>(null)
  const [showExportBanner, setShowExportBanner] = useState(false)
  const historyExportRef = useRef<HTMLDivElement>(null)

  // Voice input
  const { isListening, isSupported, transcript, error: voiceError, startListening, stopListening, resetTranscript } = useVoiceInput()

  // Sync live transcript to textarea while listening
  useEffect(() => {
    if (isListening && transcript) {
      setPrompt(transcript)
    }
  }, [transcript, isListening])

  // When listening stops, finalise transcript into textarea
  const prevListeningRef = useRef(false)
  useEffect(() => {
    if (prevListeningRef.current && !isListening && transcript) {
      setPrompt(transcript)
      resetTranscript()
      inputRef.current?.focus()
    }
    prevListeningRef.current = isListening
  }, [isListening, transcript, resetTranscript])

  // Close history export menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (historyExportRef.current && !historyExportRef.current.contains(e.target as Node)) {
        setShowHistoryExportMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleCopyResponse = (text: string, id: number) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleExportPDF = useCallback((item: AccountPrompt) => {
    exportResponseToPDF(item, accountName)
    setShowExportBanner(false)
  }, [accountName])

  const handleExportCSV = useCallback((item: AccountPrompt) => {
    exportResponseToCSV(item, accountName)
    setShowExportBanner(false)
  }, [accountName])

  const handleExportHistoryPDF = () => {
    if (history.length > 0) {
      exportHistoryToPDF(history, accountName)
    }
    setShowHistoryExportMenu(false)
  }

  const handleExportHistoryCSV = () => {
    if (history.length > 0) {
      exportHistoryToCSV(history, accountName)
    }
    setShowHistoryExportMenu(false)
  }

  // Custom markdown components for proper table rendering and heading spacing
  const markdownComponents = {
    // Headings with proper spacing
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h1 className="text-base font-bold text-[var(--text-primary)] mt-6 mb-3 pb-1 border-b border-[var(--border-color)]" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h2 className="text-sm font-bold text-[var(--text-primary)] mt-5 mb-2" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h3 className="text-sm font-semibold text-[var(--text-primary)] mt-4 mb-2" {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h4 className="text-xs font-semibold text-[var(--text-primary)] mt-3 mb-1.5 uppercase tracking-wide" {...props}>
        {children}
      </h4>
    ),
    // Paragraphs with spacing
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className="text-sm text-[var(--text-primary)] my-2 leading-relaxed" {...props}>
        {children}
      </p>
    ),
    // Lists with spacing
    ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className="text-sm text-[var(--text-primary)] my-2 ml-4 space-y-1 list-disc" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
      <ol className="text-sm text-[var(--text-primary)] my-2 ml-4 space-y-1 list-decimal" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.LiHTMLAttributes<HTMLLIElement>) => (
      <li className="text-sm text-[var(--text-primary)] leading-relaxed" {...props}>
        {children}
      </li>
    ),
    // Tables
    table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
      <div className="my-4 overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full text-sm border-collapse" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead className="bg-[rgba(68,114,196,0.15)]" {...props}>
        {children}
      </thead>
    ),
    th: ({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
      <th className="px-3 py-2 text-left text-xs font-semibold text-[var(--text-primary)] border-b border-[var(--border-color)] whitespace-nowrap" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
      <td className="px-3 py-2 text-xs text-[var(--text-secondary)] border-b border-[var(--border-color)]/50" {...props}>
        {children}
      </td>
    ),
    tr: ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
      <tr className="hover:bg-[var(--bg-tertiary)]/50 transition-colors" {...props}>
        {children}
      </tr>
    ),
    // Horizontal rules as section dividers
    hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
      <hr className="my-4 border-[var(--border-color)]" {...props} />
    ),
    // Strong/bold
    strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <strong className="font-semibold text-[var(--text-primary)]" {...props}>
        {children}
      </strong>
    ),
    // Blockquotes
    blockquote: ({ children, ...props }: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) => (
      <blockquote className="my-3 pl-4 border-l-2 border-primary/50 italic text-[var(--text-secondary)]" {...props}>
        {children}
      </blockquote>
    ),
  }

  const promptCategories = [
    {
      label: '🔍 Research & Intel',
      prompts: [
        { shortLabel: 'Pain Score Analysis', fullPrompt: 'Generate a pain score analysis for this company — rate their regulatory pressure, tech stack fragmentation, customer complaints, competitive pressure, and budget capacity on a 1-10 scale with a weighted overall score. Present ALL findings in markdown tables.' },
        { shortLabel: 'Current Tech Stack', fullPrompt: 'What is this company\'s likely current tech stack for identity verification, KYC/AML, and compliance? Present as a table with vendor, category, confidence level, and evidence columns.' },
        { shortLabel: 'Key Decision-Makers', fullPrompt: 'Identify the top 3 decision-makers I should target and suggest personalised angles for each. Present as a table with role, priority, recommended angle, and suggested opening line.' },
      ],
    },
    {
      label: '📧 Outreach & Messaging',
      prompts: [
        { shortLabel: 'Personalised Intro Email', fullPrompt: 'Draft a personalised intro email for the Head of Compliance at this company — reference a specific pain point and include a clear CTA' },
        { shortLabel: 'Multi-Touch Outreach Sequence', fullPrompt: 'Create a multi-touch outreach sequence: Day 1 email, Day 3 LinkedIn message, Day 7 value-add follow-up, Day 14 breakup email. Include a summary table of the sequence timeline.' },
        { shortLabel: 'LinkedIn Connection Messages', fullPrompt: 'Write 3 LinkedIn connection request messages (under 300 chars each) with different angles: pain-point led, trigger-based, and value-add. Present in a comparison table.' },
      ],
    },
    {
      label: '💡 Strategy & Positioning',
      prompts: [
        { shortLabel: 'Pain Points & Solutions', fullPrompt: 'What are the key pain points this company might have and how does FrankieOne specifically solve each one? Present as a table mapping each pain point to severity, FrankieOne solution, and expected impact.' },
        { shortLabel: 'Competitor Positioning', fullPrompt: 'What competitors might they be evaluating and how should I position against each? Present as a comparison table with capabilities, strengths, and our advantages.' },
        { shortLabel: 'Discovery Call Talking Points', fullPrompt: 'Suggest 5 talking points for an initial discovery call — frame as questions that uncover pain rather than pitching features. Present in a table with the question, target pain area, and ideal follow-up.' },
      ],
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-[90vw] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">AI Assistant</h2>
              <p className="text-sm text-[var(--text-muted)]">{accountName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* History Toggle + Export */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <History size={16} />
              <span>Previous Conversations</span>
              {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {history.length > 0 && (
                <span className="bg-[var(--bg-tertiary)] px-2 py-0.5 rounded-full text-xs">
                  {history.length}
                </span>
              )}
            </button>

            {/* History export dropdown */}
            {showHistory && history.length > 0 && (
              <div className="relative" ref={historyExportRef}>
                <button
                  onClick={() => setShowHistoryExportMenu(!showHistoryExportMenu)}
                  className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors px-2 py-1 rounded hover:bg-[var(--bg-tertiary)]"
                  title="Export all conversations"
                >
                  <Download size={13} />
                  <span>Export All</span>
                </button>
                {showHistoryExportMenu && (
                  <div className="absolute right-0 top-8 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
                    <button
                      onClick={handleExportHistoryPDF}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      <FileDown size={14} className="text-red-500" />
                      All conversations as PDF
                    </button>
                    <button
                      onClick={handleExportHistoryCSV}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      <FileSpreadsheet size={14} className="text-green-500" />
                      All conversations as CSV
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* History Section */}
          {showHistory && (
            <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] overflow-hidden">
              {isLoadingHistory ? (
                <div className="p-4 text-center text-[var(--text-muted)]">
                  <Loader2 size={20} className="animate-spin mx-auto" />
                </div>
              ) : history.length === 0 ? (
                <div className="p-4 text-center text-[var(--text-muted)] text-sm">
                  No previous conversations
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto divide-y divide-[var(--border-color)]">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 hover:bg-[var(--bg-tertiary)] cursor-pointer transition-colors"
                      onClick={() => {
                        setCurrentResponse(item)
                        setShowHistory(false)
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-[var(--text-primary)] line-clamp-2">{item.prompt}</p>
                        {item.hubspot_note_id && (
                          <span title="Logged to HubSpot">
                            <ExternalLink size={14} className="text-[var(--text-muted)] flex-shrink-0" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {new Date(item.created_at).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Current Response */}
          {currentResponse && (
            <div ref={responseRef} className="space-y-4">
              {/* User Question */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-primary" />
                </div>
                <div className="flex-1 bg-[var(--bg-secondary)] rounded-lg p-4">
                  <p className="text-sm text-[var(--text-primary)]">{currentResponse.prompt}</p>
                </div>
              </div>

              {/* AI Response */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <Bot size={16} className="text-white" />
                </div>
                <div className="flex-1 bg-[var(--bg-secondary)] rounded-lg p-4">
                  <div className="ai-response-content max-w-none
                    prose-code:text-xs prose-code:bg-[var(--bg-tertiary)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[var(--text-primary)]
                    prose-em:text-[var(--text-secondary)]
                  ">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {currentResponse.response}
                    </ReactMarkdown>
                  </div>
                  {/* Format Detection Banner */}
                  {showExportBanner && detectedFormat && (
                    <div className="mt-3 flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/10 border border-primary/20">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {detectedFormat === 'pdf' ? 'Your report is ready' : 'Your data is ready'}
                      </span>
                      <button
                        onClick={() => {
                          detectedFormat === 'pdf' ? handleExportPDF(currentResponse) : handleExportCSV(currentResponse)
                        }}
                        className={clsx(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors',
                          detectedFormat === 'pdf' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                        )}
                      >
                        {detectedFormat === 'pdf' ? <FileDown size={14} /> : <FileSpreadsheet size={14} />}
                        Download as {detectedFormat.toUpperCase()}
                      </button>
                      <button
                        onClick={() => setShowExportBanner(false)}
                        className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  {/* Export Action Bar */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border-color)]">
                    <button
                      onClick={() => handleExportPDF(currentResponse)}
                      className={clsx(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        detectedFormat === 'pdf' && !showExportBanner
                          ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-color)]'
                      )}
                    >
                      <FileDown size={16} />
                      Download PDF
                    </button>
                    <button
                      onClick={() => handleExportCSV(currentResponse)}
                      className={clsx(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        detectedFormat === 'csv' && !showExportBanner
                          ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-color)]'
                      )}
                    >
                      <FileSpreadsheet size={16} />
                      Download CSV
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={() => handleCopyResponse(currentResponse.response, currentResponse.id)}
                      className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors px-2 py-1 rounded hover:bg-[var(--bg-tertiary)]"
                      title="Copy response as markdown"
                    >
                      {copiedId === currentResponse.id ? (
                        <>
                          <Check size={12} className="text-green-500" />
                          <span className="text-green-500">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy size={12} />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-[var(--text-muted)]">
                      {currentResponse.tokens_used} tokens
                    </span>
                    {currentResponse.hubspot_note_id && (
                      <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <ExternalLink size={12} />
                        Logged to HubSpot
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Suggested Prompts (show when no current response) */}
          {!currentResponse && !isLoading && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-[var(--text-secondary)]">What would you like to know?</p>
              {promptCategories.map((category, catIdx) => (
                <div key={catIdx} className="space-y-2">
                  <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{category.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {category.prompts.map((suggested, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setPrompt(suggested.fullPrompt)
                          inputRef.current?.focus()
                        }}
                        className="text-sm px-3 py-1.5 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-full text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all hover:border-primary/30"
                      >
                        {suggested.shortLabel}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="text-white" />
              </div>
              <div className="flex-1 bg-[var(--bg-secondary)] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[var(--text-muted)]">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Input Section */}
        <div className="border-t border-[var(--border-color)] p-4">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* HubSpot Toggle */}
            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer">
              <input
                type="checkbox"
                checked={logToHubspot}
                onChange={(e) => setLogToHubspot(e.target.checked)}
                className="rounded border-[var(--border-color)] text-primary focus:ring-primary"
              />
              <span>Log to HubSpot activity</span>
            </label>

            {/* Input */}
            <div className="relative">
              <textarea
                ref={inputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? 'Listening... speak now' : 'Ask anything about this account...'}
                rows={3}
                className={clsx(
                  'w-full px-4 py-3 rounded-lg border bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
                  'border-[var(--border-color)]',
                  isSupported ? 'pr-24' : 'pr-12',
                  isListening && 'ring-2 ring-red-500/50 border-red-500/50'
                )}
                disabled={isLoading}
              />
              {/* Voice input button */}
              {isSupported && (
                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading}
                  className={clsx(
                    'absolute right-14 bottom-3 p-2 rounded-lg transition-all',
                    isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                  )}
                  title={isListening ? 'Stop recording' : 'Voice input'}
                >
                  <Mic size={18} />
                </button>
              )}
              <button
                type="submit"
                disabled={!prompt.trim() || isLoading}
                className={clsx(
                  'absolute right-3 bottom-3 p-2 rounded-lg transition-colors',
                  prompt.trim() && !isLoading
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed'
                )}
              >
                <Send size={18} />
              </button>
            </div>

            {/* Voice status / hint */}
            {isListening ? (
              <p className="text-xs text-red-500 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Listening... speak now. Click the mic to stop.
              </p>
            ) : voiceError ? (
              <p className="text-xs text-red-500">{voiceError}</p>
            ) : (
              <p className="text-xs text-[var(--text-muted)]">
                Press Enter to send, Shift+Enter for new line{isSupported ? ', or click the mic to use voice' : ''}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
